import React from "react";

import PropTypes from "prop-types";
import Badge from "./Badge";
import { cn } from "../util";
import SkeletonLoader from "./SkeletonLoader";

export function CardSection({
  children,
  title = "Title",
  step = null,
  status = "",
  footer = null,
}) {
  return (
    <section className="flex flex-col gap-y-8 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
      <header className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
        {step && (
          <span className="flex items-center justify-center pr-6 mr-6 border-r-2 border-gray-100 h-full">
            {step}
          </span>
        )}
        <div className="flex items-center gap-2 flex-1">
          <h3 className="flex-1">{title}</h3>
          {status && <Badge text={status} />}
        </div>
      </header>

      <main className="px-6">
        <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          {children}
        </div>
      </main>

      {footer && (
        <>
          <span className="h-[1px] mx-6 col-span-full bg-gray-200" />
          <footer className="self-end px-6 space-x-4">{footer}</footer>
        </>
      )}
    </section>
  );
}

export function CardSectionWrapper({ children, className = "" }) {
  return (
    <div className={cn("grid grid-cols-1 gap-4 w-full", className)}>
      {children}
    </div>
  );
}

export function CardItem({ label, value, placeholder, className, children }) {
  return (
    <div className={cn("sm:col-span-1", className)}>
      <>
        {label && <p className="text-sm font-medium text-gray-500">{label}</p>}
        {children && <div className="mt-1">{children}</div>}
        {!children && (
          <span className="mt-1 text-sm text-gray-900 font-normal">
            {value ? (
              <p className="mt-1 text-sm text-gray-900 font-normal">{value}</p>
            ) : (
              <p className="mt-1 text-sm font-normal text-gray-500">
                {placeholder || "No disponible"}
              </p>
            )}
          </span>
        )}
      </>
    </div>
  );
}

export function CardSectionSkeleton({ title, step }) {
  return (
    <div className="flex flex-col gap-y-8 pb-8 bg-white border border-gray-100 shadow-md rounded-lg">
      <div className="flex items-center h-[64px] px-6 border-b-2 border-gray-100 font-medium text-lg text-gray-900">
        {step && (
          <span className="flex items-center justify-center pr-6 mr-6 border-r-2 border-gray-100 h-full">
            {step}
          </span>
        )}
        <h3 className="flex-1">{title}</h3>
        <SkeletonLoader size="sm" width="w-16" />
      </div>

      <div className="px-6">
        <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          <SkeletonLoader size="sm" width="col-span-1" />
          <SkeletonLoader size="sm" width="col-span-1" />
        </div>
      </div>
    </div>
  );
}

CardSection.propTypes = {
  title: PropTypes.string,
  step: PropTypes.string,
  status: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
};

CardSectionWrapper.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

CardItem.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

CardSectionSkeleton.propTypes = {
  title: PropTypes.string,
  step: PropTypes.string,
};
